import { Copy } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { t } from '@/i18n';
import type { KsefMetadata } from '@ip/shared-types';

interface KsefMetadataPanelProps {
  ksefNumber: string | null;
  ksefVerificationUrl: string | null;
  ksefMetadata: KsefMetadata | null;
  customerName: string | null;
  customerNip: string | null;
  companyNip: string;
  correctedInvoices?: Array<{
    id: string | null;
    invoiceNumber: string | null;
    ksefNumber: string | null;
  }> | null;
}

const formatNumber = (value: number): string =>
  value.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

type AdresData = {
  kodKraju?: string;
  adresL1?: string;
  adresL2?: string;
  ulica?: string;
  nrDomu?: string;
  nrLokalu?: string;
  kodPocztowy?: string;
  miejscowosc?: string;
};

const formatAddress = (addr?: AdresData): string | null => {
  if (!addr) return null;
  if (addr.ulica) {
    const street = [addr.ulica, addr.nrDomu, addr.nrLokalu].filter(Boolean).join(' ');
    const postal = [addr.kodPocztowy, addr.miejscowosc].filter(Boolean).join(' ');
    return [street, postal].filter(Boolean).join(', ');
  }
  return [addr.adresL1, addr.adresL2].filter(Boolean).join(', ') || null;
};

const RODZAJ_CODES = ['VAT', 'KOR', 'ZAL', 'ROZ', 'UPR', 'ROD', 'WEW', 'VAT_MP', 'VAT_RR'];
const rodzajLabel = (code: string): string =>
  RODZAJ_CODES.includes(code) ? t(`ksef.metadata.rodzajLabels.${code}`) : code;

const WARIANT_CODES = [1, 2, 3, 4, 5, 6];
const wariantLabel = (n: number): string =>
  WARIANT_CODES.includes(n) ? t(`ksef.metadata.wariantLabels.${n}`) : t('ksef.metadata.wariantN', { n });

const ADNOTACJA_CODES = [
  'P_16', 'P_17', 'P_18', 'P_18A', 'P_19', 'P_19A', 'P_19B', 'P_19C',
  'P_20', 'P_21', 'P_22', 'P_23', 'P_106E_2', 'P_106E_3', 'zwolnienieP19A',
];
const adnotacjaLabel = (key: string): string =>
  ADNOTACJA_CODES.includes(key) ? t(`ksef.metadata.adnotacjeLabels.${key}`) : key;

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t px-4 py-3">
      <p className="mb-2 text-sm font-semibold">{title}</p>
      {children}
    </div>
  );
}

function DescGrid({ cols = 1, children }: { cols?: 1 | 2; children: React.ReactNode }) {
  return <div className={cols === 2 ? 'grid grid-cols-2 gap-x-4 gap-y-1' : 'space-y-1'}>{children}</div>;
}

function DescItem({ label, span, children }: { label: string; span?: boolean; children: React.ReactNode }) {
  return (
    <div className={`text-sm ${span ? 'col-span-2' : ''}`}>
      <span className="text-muted-foreground">{label}: </span>
      <span>{children}</span>
    </div>
  );
}

function CopyableText({ value, className }: { value: string; className?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 ${className ?? ''}`}>
      <span className="font-mono text-sm break-all">{value}</span>
      <button
        type="button"
        aria-label={t('ksef.metadata.copy')}
        className="text-muted-foreground hover:text-foreground shrink-0"
        onClick={() => {
          navigator.clipboard.writeText(value);
          toast.success(t('ksef.metadata.copied'));
        }}
      >
        <Copy className="size-3.5" />
      </button>
    </span>
  );
}

export function KsefMetadataPanel({
  ksefNumber,
  ksefVerificationUrl: verificationUrl,
  ksefMetadata: meta,
  customerName,
  customerNip,
  companyNip,
  correctedInvoices,
}: KsefMetadataPanelProps) {
  if (!meta) return null;

  return (
    <div className="flex flex-col">
      {/* QR / Verification Section */}
      {(ksefNumber || verificationUrl) && (
        <Section title={t('ksef.metadata.verification')}>
          <div className="flex items-start gap-4">
            {verificationUrl && (
              <div className="rounded-md bg-white p-2">
                <QRCodeSVG value={verificationUrl} size={120} level="M" />
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              {ksefNumber && <CopyableText value={ksefNumber} />}
              {verificationUrl && (
                <a
                  href={verificationUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary text-sm underline-offset-2 hover:underline"
                >
                  {t('ksef.metadata.verifyOnKsef')}
                </a>
              )}
            </div>
          </div>
        </Section>
      )}

      {/* Invoice Metadata */}
      <Section title={t('ksef.metadata.invoiceData')}>
        <DescGrid cols={2}>
          <DescItem label={t('ksef.metadata.form')}>
            {meta.formType}{meta.xmlVersion ? ` v${meta.xmlVersion}` : ''}
          </DescItem>
          {meta.rodzajFaktury && (
            <DescItem label={t('ksef.metadata.rodzaj')}>{rodzajLabel(meta.rodzajFaktury)}</DescItem>
          )}
          {meta.wariantFormularza && (
            <DescItem label={t('ksef.metadata.wariant')}>
              {wariantLabel(meta.wariantFormularza)}
            </DescItem>
          )}
          {meta.miejsceWystawienia && (
            <DescItem label={t('ksef.metadata.placeOfIssue')}>{meta.miejsceWystawienia}</DescItem>
          )}
          {meta.dataWytworzeniaFa && <DescItem label={t('ksef.metadata.xmlDate')}>{meta.dataWytworzeniaFa}</DescItem>}
          {meta.systemInfo && <DescItem label={t('ksef.metadata.system')}>{meta.systemInfo}</DescItem>}
          {(meta.okresOd || meta.okresDo) && (
            <DescItem label={t('ksef.metadata.period')} span>
              {meta.okresOd}{meta.okresDo ? ` – ${meta.okresDo}` : ''}
            </DescItem>
          )}
        </DescGrid>
      </Section>

      {/* Correction linkage (2026-07-13 spec) */}
      {(meta.daneFaKorygowanej?.length || meta.przyczynaKorekty) && (
        <Section title={t('ksef.metadata.correction')}>
          <DescGrid cols={1}>
            {(correctedInvoices ??
              (meta.daneFaKorygowanej ?? []).map((ref) => ({
                id: null,
                invoiceNumber: ref.nrFaKorygowanej ?? null,
                ksefNumber: ref.nrKsefFaKorygowanej ?? null,
              }))
            ).map((corrected, i) => (
              <DescItem key={i} label={t('ksef.metadata.corrects')}>
                {corrected.id ? (
                  <Link
                    to={`/${companyNip}/invoices/${corrected.id}`}
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    {corrected.invoiceNumber || corrected.ksefNumber}
                  </Link>
                ) : (
                  <span>{corrected.invoiceNumber || corrected.ksefNumber || '—'}</span>
                )}
              </DescItem>
            ))}
            {meta.przyczynaKorekty && (
              <DescItem label={t('ksef.metadata.correctionReason')}>{meta.przyczynaKorekty}</DescItem>
            )}
          </DescGrid>
        </Section>
      )}

      {/* Buyer / Podmiot2 */}
      {(customerName || customerNip || meta.podmiot2) && (
        <Section title={t('ksef.metadata.buyer')}>
          <DescGrid>
            {customerName && <DescItem label={t('ksef.metadata.name')}>{customerName}</DescItem>}
            {customerNip && <DescItem label={t('ksef.metadata.nip')}>{customerNip}</DescItem>}
            {meta.podmiot2?.nrKlienta && <DescItem label={t('ksef.metadata.clientNo')}>{meta.podmiot2.nrKlienta}</DescItem>}
            {meta.podmiot2?.adres && formatAddress(meta.podmiot2.adres) && (
              <DescItem label={t('ksef.metadata.address')}>{formatAddress(meta.podmiot2.adres)}</DescItem>
            )}
            {meta.podmiot2?.kontakt?.email && (
              <DescItem label={t('ksef.metadata.email')}>{meta.podmiot2.kontakt.email}</DescItem>
            )}
            {meta.podmiot2?.kontakt?.telefon && (
              <DescItem label={t('ksef.metadata.phone')}>{meta.podmiot2.kontakt.telefon}</DescItem>
            )}
          </DescGrid>
        </Section>
      )}

      {/* Third Party / Podmiot3 */}
      {meta.podmiot3 && (
        <Section title={t('ksef.metadata.thirdParty')}>
          <DescGrid>
            {meta.podmiot3.nazwa && <DescItem label={t('ksef.metadata.name')}>{meta.podmiot3.nazwa}</DescItem>}
            {meta.podmiot3.nip && <DescItem label={t('ksef.metadata.nip')}>{meta.podmiot3.nip}</DescItem>}
            {meta.podmiot3.adres && formatAddress(meta.podmiot3.adres) && (
              <DescItem label={t('ksef.metadata.address')}>{formatAddress(meta.podmiot3.adres)}</DescItem>
            )}
          </DescGrid>
        </Section>
      )}

      {/* VAT Rate Breakdown */}
      {meta.stawkiVat && meta.stawkiVat.length > 0 && (
        <Section title={t('ksef.metadata.vatBreakdown')}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">{t('ksef.metadata.rate')}</TableHead>
                <TableHead className="text-right">{t('ksef.metadata.net')}</TableHead>
                <TableHead className="text-right">{t('ksef.metadata.vat')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {meta.stawkiVat.map((row) => {
                const num = parseFloat(row.stawka);
                const stawkaLabel = isNaN(num) ? row.stawka : `${row.stawka}%`;
                return (
                  <TableRow key={row.stawka}>
                    <TableCell>{stawkaLabel}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatNumber(row.netto)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatNumber(row.vat)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Section>
      )}

      {/* Bank Accounts */}
      {(meta.rachunkiBankowe?.length || meta.rachunekBankowyMP || meta.zaplacono !== undefined) && (
        <Section title={t('ksef.metadata.bankAccounts')}>
          <div className="space-y-2">
            {meta.rachunkiBankowe?.map((rb, i) => (
              <div key={i} className="space-y-0.5">
                <CopyableText value={rb.nrRB} />
                {rb.nazwaBanku && <p className="text-muted-foreground text-xs">{rb.nazwaBanku}</p>}
                {rb.swift && <p className="text-muted-foreground text-xs">SWIFT: {rb.swift}</p>}
              </div>
            ))}
            {meta.rachunekBankowyMP && (
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                >
                  MPP
                </Badge>
                <CopyableText value={meta.rachunekBankowyMP.nrRB} />
              </div>
            )}
            {/* Zaplacono is the FA(3) FLAG "settled in full" (1), not an amount —
                it used to render as a baffling "Zapłacono: 1,00". */}
            {meta.zaplacono === 1 && (
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className="border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                >
                  {t('ksef.metadata.paid')}
                </Badge>
                {meta.dataZaplaty && (
                  <span className="text-muted-foreground text-sm">{meta.dataZaplaty}</span>
                )}
              </div>
            )}
            {meta.platnoscInna && (
              <p className="text-muted-foreground text-sm">{t('ksef.metadata.otherPayment', { value: meta.platnoscInna })}</p>
            )}
          </div>
        </Section>
      )}

      {/* Annotations */}
      {meta.adnotacje && Object.keys(meta.adnotacje).length > 0 && (
        <Section title={t('ksef.metadata.annotations')}>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(meta.adnotacje).map(([key, value]) => {
              if (!value && value !== 0) return null;
              // Boolean-like flags (1/2 in KSeF means tak/nie)
              if (value === 2) return null; // 2 = "nie" in KSeF annotations
              const label = adnotacjaLabel(key);
              const displayValue =
                typeof value === 'object' ? JSON.stringify(value) : value === 1 ? '' : `: ${String(value)}`;
              return (
                <Badge
                  key={key}
                  variant="outline"
                  className="border-indigo-500/40 bg-indigo-500/10 text-indigo-700 dark:text-indigo-400"
                >
                  {label}
                  {displayValue}
                </Badge>
              );
            })}
          </div>
        </Section>
      )}

      {/* Additional Descriptions */}
      {meta.dodatkowyOpis && meta.dodatkowyOpis.length > 0 && (
        <Section title={t('ksef.metadata.additionalDescription')}>
          <DescGrid>
            {meta.dodatkowyOpis.map((item, i) => (
              <DescItem key={i} label={item.klucz}>
                {item.wartosc}
              </DescItem>
            ))}
          </DescGrid>
        </Section>
      )}

      {/* Order References */}
      {meta.zamowienie && (
        <Section title={t('ksef.metadata.order')}>
          <div className="space-y-1">
            {meta.zamowienie.wartoscZamowienia !== undefined && (
              <p className="text-muted-foreground text-sm">
                {t('ksef.metadata.orderValue', { value: formatNumber(meta.zamowienie.wartoscZamowienia) })}
              </p>
            )}
            {meta.zamowienie.wiersze?.map((w, i) => (
              <div key={i} className="flex flex-wrap items-baseline gap-2 text-sm">
                {w.nrZamowieniaObcy && <span>{t('ksef.metadata.orderExternalNo', { value: w.nrZamowieniaObcy })}</span>}
                {w.nrZamowienia && <span>{t('ksef.metadata.orderInternalNo', { value: w.nrZamowienia })}</span>}
                {w.dataZamowienia && <span className="text-muted-foreground">({w.dataZamowienia})</span>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Delivery Notes */}
      {meta.wz && meta.wz.length > 0 && (
        <Section title={t('ksef.metadata.deliveryNotes')}>
          <div className="space-y-1">
            {meta.wz.map((w, i) => (
              <div key={i} className="flex flex-wrap items-baseline gap-2 text-sm">
                <span>{w.nrWZ}</span>
                {w.dataWZ && <span className="text-muted-foreground">({w.dataWZ})</span>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Charges / deductions / amount actually owed (Fa/Rozliczenie).
          `doZaplaty` is the number that matters here: it is what the buyer
          owes once the issuer's charges and deductions are applied, so it can
          legitimately differ from the invoice total shown everywhere else. */}
      {meta.rozliczenie && (
        <Section title={t('ksef.metadata.settlement')}>
          {(meta.rozliczenie.obciazenia?.length || meta.rozliczenie.odliczenia?.length) ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('ksef.metadata.settlement')}</TableHead>
                  <TableHead>{t('ksef.metadata.reason')}</TableHead>
                  <TableHead className="text-right">{t('ksef.metadata.amount')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {meta.rozliczenie.obciazenia?.map((item, index) => (
                  <TableRow key={`charge-${index}`}>
                    <TableCell>{t('ksef.metadata.charges')}</TableCell>
                    <TableCell>{item.powod || '-'}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatNumber(item.kwota)}</TableCell>
                  </TableRow>
                ))}
                {meta.rozliczenie.odliczenia?.map((item, index) => (
                  <TableRow key={`deduction-${index}`}>
                    <TableCell>{t('ksef.metadata.deductions')}</TableCell>
                    <TableCell>{item.powod || '-'}</TableCell>
                    <TableCell className="text-right tabular-nums">-{formatNumber(item.kwota)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
          <DescGrid cols={2}>
            {meta.rozliczenie.sumaObciazen !== undefined && (
              <DescItem label={t('ksef.metadata.totalCharges')}>{formatNumber(meta.rozliczenie.sumaObciazen)}</DescItem>
            )}
            {meta.rozliczenie.sumaOdliczen !== undefined && (
              <DescItem label={t('ksef.metadata.totalDeductions')}>{formatNumber(meta.rozliczenie.sumaOdliczen)}</DescItem>
            )}
            {meta.rozliczenie.doZaplaty !== undefined && (
              <DescItem label={t('ksef.metadata.amountDue')}>{formatNumber(meta.rozliczenie.doZaplaty)}</DescItem>
            )}
            {meta.rozliczenie.doRozliczenia !== undefined && (
              <DescItem label={t('ksef.metadata.amountToRefund')}>{formatNumber(meta.rozliczenie.doRozliczenia)}</DescItem>
            )}
          </DescGrid>
        </Section>
      )}

      {/* Seller Contact */}
      {meta.podmiot1Kontakt && (meta.podmiot1Kontakt.email || meta.podmiot1Kontakt.telefon) && (
        <Section title={t('ksef.metadata.sellerContact')}>
          <DescGrid>
            {meta.podmiot1Kontakt.email && <DescItem label={t('ksef.metadata.email')}>{meta.podmiot1Kontakt.email}</DescItem>}
            {meta.podmiot1Kontakt.telefon && (
              <DescItem label={t('ksef.metadata.phone')}>{meta.podmiot1Kontakt.telefon}</DescItem>
            )}
          </DescGrid>
        </Section>
      )}

      {/* Footer */}
      {meta.stopka && (
        <div className="border-t px-4 py-3">
          <p className="text-muted-foreground text-xs">{meta.stopka}</p>
        </div>
      )}
    </div>
  );
}
